/**
 * Stage 6: Review & Policy Acceptance
 *
 * Final review of booking details and acceptance of policies
 * Creates audit trail for terms of service, cancellation policy, privacy policy
 *
 * Location: src/components/booking/flow/stages/Stage6Review.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 *
 * Features:
 * - Complete booking summary
 * - Policy checkboxes (required)
 * - Cancellation policy details
 * - Clear acceptance language
 */

import React from 'react';
import { formatDisplayDate } from '../../../../utils/formHelpers';
import PricingSummary from '../PricingSummary';
import Avatar from '../../../common/Avatar';
import { useCart } from '../../../../contexts/CartContext';

function isConsecutiveBookingEnabled() {
  return import.meta.env.VITE_FEATURE_CONSECUTIVE_BOOKING === 'true';
}

/**
 * Stage6Review Component
 *
 * @param {Object} props
 * @param {Object} props.experience - Experience details
 * @param {Object} props.bookingData - Current booking form data
 * @param {Function} props.onChange - Handler for input changes
 * @param {Object} props.errors - Validation errors
 * @param {Object} props.pricing - Calculated pricing
 * @returns {JSX.Element}
 */
const Stage6Review = ({
  experience,
  bookingData,
  onChange,
  errors = {},
  pricing
}) => {
  const cart = useCart();
  const cartItems = isConsecutiveBookingEnabled() ? cart.items : [];
  const hasCartItems = cartItems.length > 0;

  const formatPrice = (cents) => {
    if (!cents) { return '$0.00'; }
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review Your Booking</h2>
        <p className="text-gray-600 mt-2">
          Please review your booking details and accept the policies to continue
        </p>
      </div>

      {/* Booking Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-gray-900">Booking Details</h3>

        {/* Experience */}
        <div className="flex items-start gap-4 pb-3 border-b border-gray-200">
          <Avatar
            src={experience.guidePhoto}
            name={experience.guideName}
            size="lg"
          />
          <div className="flex-1">
            <p className="font-medium text-gray-900">{experience.name}</p>
            <p className="text-sm text-gray-600">with {experience.guideName}</p>
          </div>
        </div>

        {/* SCRUM-709: Cart line items when consecutive booking is active */}
        {hasCartItems ? (
          <div className="space-y-2 text-sm">
            <h4 className="font-medium text-gray-900">Selected Dates</h4>
            {cartItems.map(item => (
              <div key={item.date} className="flex justify-between items-center py-1">
                <span className="text-gray-700">{formatDisplayDate(item.date)}</span>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{formatPrice(item.priceCents)}</span>
                  <button
                    type="button"
                    onClick={() => cart.removeDate(item.date)}
                    className="text-red-500 text-xs hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-between font-semibold border-t border-gray-300 pt-2">
              <span>Subtotal ({cartItems.length} {cartItems.length === 1 ? 'day' : 'days'})</span>
              <span>{formatPrice(cart.totals.subtotalCents)}</span>
            </div>
          </div>
        ) : (
        /* Trip Details — existing single-booking display */
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Date:</span>
            <span className="font-medium text-gray-900">
              {bookingData.startDate ? formatDisplayDate(bookingData.startDate) : 'Not selected'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Guests:</span>
            <span className="font-medium text-gray-900">
              {bookingData.partySize} {bookingData.partySize === 1 ? 'guest' : 'guests'}
            </span>
          </div>
          {bookingData.phoneNumber && (
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium text-gray-900">{bookingData.phoneNumber}</span>
            </div>
          )}
          {bookingData.smsConsent && (
            <div className="flex items-center gap-2 text-emerald-600 text-xs">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              SMS notifications enabled
            </div>
          )}
        </div>
        )}

        {/* Guests (shown for both cart and non-cart flows) */}
        {hasCartItems && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Guests:</span>
              <span className="font-medium text-gray-900">
                {bookingData.partySize} {bookingData.partySize === 1 ? 'guest' : 'guests'}
              </span>
            </div>
          </div>
        )}

        {/* Special Requirements */}
        {bookingData.specialRequirements && (
          <div className="pt-3 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-900 mb-1">Special Requirements:</p>
            <p className="text-sm text-gray-600 italic">&quot;{bookingData.specialRequirements}&quot;</p>
          </div>
        )}
      </div>

      {/* Pricing Summary */}
      {pricing && (
        <PricingSummary
          perPersonCents={pricing.perPerson}
          numParticipants={bookingData.partySize || 1}
          subtotalCents={pricing.subtotal}
          platformFeeCents={pricing.platformFee}
          taxAmountCents={pricing.taxAmountCents || 0}
          totalCents={pricing.total + (pricing.taxAmountCents || 0)}
        />
      )}

      {/* Cancellation Policy */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-semibold text-amber-900 mb-2">Cancellation Policy</h3>
        {experience.cancellationPolicy ? (
          <div className="text-sm text-amber-800 space-y-1">
            <p>
              <strong>{experience.cancellationPolicy.tier} Cancellation Policy</strong>
            </p>
            <p>
              • Full refund if cancelled {experience.cancellationPolicy.fullRefundDays}+ days before trip
            </p>
            <p>
              • {experience.cancellationPolicy.partialRefundPercent}% refund if cancelled{' '}
              {experience.cancellationPolicy.partialRefundDays}-{experience.cancellationPolicy.fullRefundDays - 1} days before
            </p>
            <p>
              • No refund for cancellations within {experience.cancellationPolicy.partialRefundDays} days
            </p>
          </div>
        ) : (
          <p className="text-sm text-amber-800">
            Please review the cancellation policy before booking.
          </p>
        )}
      </div>

      {/* Policy Acceptances */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Required Agreements</h3>

        {/* Terms of Service */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="termsAccepted"
            name="termsAccepted"
            checked={bookingData.termsAccepted || false}
            onChange={(e) => onChange('termsAccepted', e.target.checked)}
            required
            className={`mt-1 w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500 ${
              errors.termsAccepted ? 'border-red-500' : ''
            }`}
          />
          <label htmlFor="termsAccepted" className="text-sm text-gray-700 cursor-pointer">
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
              Privacy Policy
            </a>
            <span className="text-red-500 ml-1">*</span>
          </label>
        </div>
        {errors.termsAccepted && (
          <p className="text-sm text-red-600 ml-7">{errors.termsAccepted}</p>
        )}

        {/* Cancellation Policy */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="policyAccepted"
            name="policyAccepted"
            checked={bookingData.policyAccepted || false}
            onChange={(e) => onChange('policyAccepted', e.target.checked)}
            required
            className={`mt-1 w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500 ${
              errors.policyAccepted ? 'border-red-500' : ''
            }`}
          />
          <label htmlFor="policyAccepted" className="text-sm text-gray-700 cursor-pointer">
            I understand and accept the{' '}
            <a href="/cancellation-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
              Cancellation Policy
            </a>
            {' '}and agree to the refund terms
            <span className="text-red-500 ml-1">*</span>
          </label>
        </div>
        {errors.policyAccepted && (
          <p className="text-sm text-red-600 ml-7">{errors.policyAccepted}</p>
        )}
      </div>

    </div>
  );
};

export default Stage6Review;
